import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { getMobileOS } from '../utils/deviceDetection';

export default function InviteRedirectPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'redirecting' | 'manual' | 'desktop' | 'error'>('redirecting');
  const [inviteCode, setInviteCode] = useState<string | null>(null);

  useEffect(() => {
    // Check for both partner and user invite codes
    const partnerCode = searchParams.get('partner');
    const userInviteCode = searchParams.get('invite') || searchParams.get('code'); // Support both formats
    
    const code = partnerCode || userInviteCode;
    const isPartnerInvite = !!partnerCode;
    
    if (!code) {
      setStatus('error');
      return;
    }

    setInviteCode(code);
    
    const mobileOS = getMobileOS();
    
    console.log('🔗 Invite code:', code);
    console.log('🏢 Is partner invite:', isPartnerInvite);
    console.log('📱 Detected OS:', mobileOS || 'desktop');
    
    // If desktop/non-mobile, show desktop-specific message
    if (!mobileOS) {
      console.log('💻 Desktop detected - showing mobile-only message');
      setStatus('desktop');
      return;
    }
    
    // Build deep link based on invite type
    const deepLink = isPartnerInvite
      ? `referpal://auth/register?partner=${code}` // Partner invite - any user type
      : `referpal://auth/register?invite_code=${code}&role=referrer`; // User invite - referrer only
    
    const appStoreLink = 'https://apps.apple.com/app/referpal';
    const playStoreLink = 'https://play.google.com/store/apps/details?id=com.referpal.app';
    
    // Only attempt redirect for mobile users
    console.log('📱 Mobile detected - attempting app redirect');
    window.location.href = deepLink;
    
    // Set up fallback timer
    const fallbackTimer = setTimeout(() => {
      console.log('⚠️ App not detected after 3 seconds');
      setStatus('manual');
      
      // Optionally redirect to app store
      if (mobileOS === 'ios') {
        // Uncomment when app is published
        // window.location.href = appStoreLink;
        console.log('Would redirect to App Store:', appStoreLink);
      } else if (mobileOS === 'android') {
        // Uncomment when app is published
        // window.location.href = playStoreLink;
        console.log('Would redirect to Play Store:', playStoreLink);
      }
    }, 3000);
    
    // Clear timeout if window loses focus (app opened)
    const handleBlur = () => {
      console.log('✅ Window blurred - app likely opened');
      clearTimeout(fallbackTimer);
    };
    
    window.addEventListener('blur', handleBlur);
    
    return () => {
      clearTimeout(fallbackTimer);
      window.removeEventListener('blur', handleBlur);
    };
  }, [searchParams]);

  const handleManualOpen = () => {
    if (!inviteCode) return;
    
    // Check if partner or user invite
    const partnerCode = searchParams.get('partner');
    const isPartnerInvite = !!partnerCode;
    
    const deepLink = isPartnerInvite
      ? `referpal://auth/register?partner=${inviteCode}`
      : `referpal://auth/register?invite_code=${inviteCode}&role=referrer`;
    
    window.location.href = deepLink;
  };

  const handleDownloadApp = () => {
    const mobileOS = getMobileOS();
    const appStoreLink = 'https://apps.apple.com/app/referpal';
    const playStoreLink = 'https://play.google.com/store/apps/details?id=com.referpal.app';
    
    if (mobileOS === 'ios') {
      window.location.href = appStoreLink;
    } else if (mobileOS === 'android') {
      window.location.href = playStoreLink;
    } else {
      alert('Please open this link on your mobile device to download the app.');
    }
  };

  const handleCopyLink = () => {
    const currentUrl = window.location.href;
    navigator.clipboard.writeText(currentUrl).then(() => {
      alert('Link copied to clipboard! You can now share it with your mobile device.');
    }).catch(() => {
      alert('Failed to copy link. Please copy the URL manually.');
    });
  };

  if (status === 'desktop') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
          <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-10 h-10 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
            </svg>
          </div>
          
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Open on Mobile Device
          </h1>
          
          <p className="text-gray-600 mb-6">
            This invite link is for the ReferPal mobile app. Please open this link on your mobile phone to continue.
          </p>

          {inviteCode && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-emerald-800 font-medium mb-1">Your Invite Code</p>
              <p className="text-2xl font-bold text-emerald-900 tracking-wider">{inviteCode}</p>
              <p className="text-xs text-emerald-700 mt-2">Save this code to use when registering</p>
            </div>
          )}

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 text-left">
            <p className="text-sm font-medium text-blue-900 mb-2">📱 How to proceed:</p>
            <ol className="text-sm text-blue-800 space-y-2 list-decimal list-inside">
              <li>Copy this link using the button below</li>
              <li>Send it to your mobile phone (email, message, etc.)</li>
              <li>Open the link on your phone</li>
              <li>The ReferPal app will open automatically</li>
            </ol>
          </div>

          <div className="space-y-3">
            <button
              onClick={handleCopyLink}
              className="w-full bg-emerald-600 text-white py-3 px-6 rounded-lg font-medium hover:bg-emerald-700 transition-colors"
            >
              Copy Link
            </button>
            
            <p className="text-xs text-gray-500">
              Don't have the app yet? Download ReferPal from the App Store or Google Play Store.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Invalid Invite Link</h1>
          <p className="text-gray-600 mb-6">
            This invite link is missing a valid invite code. Please ask your friend to share the invite link again.
          </p>
          <button
            onClick={() => navigate('/')}
            className="w-full bg-emerald-600 text-white py-3 px-6 rounded-lg font-medium hover:bg-emerald-700 transition-colors"
          >
            Go to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
        <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <svg className="w-10 h-10 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
          </svg>
        </div>
        
        <h1 className="text-2xl font-bold text-gray-900 mb-2">
          Opening ReferPal...
        </h1>
        
        <p className="text-gray-600 mb-6">
          {status === 'redirecting' 
            ? 'You\'re being redirected to the ReferPal app with invite code.' 
            : 'If the app didn\'t open automatically, use the button below.'}
        </p>

        {inviteCode && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-emerald-800 font-medium mb-1">Your Invite Code</p>
            <p className="text-2xl font-bold text-emerald-900 tracking-wider">{inviteCode}</p>
          </div>
        )}

        {status === 'manual' && (
          <div className="space-y-3">
            <button
              onClick={handleManualOpen}
              className="w-full bg-emerald-600 text-white py-3 px-6 rounded-lg font-medium hover:bg-emerald-700 transition-colors"
            >
              Open ReferPal App
            </button>
            
            <button
              onClick={handleDownloadApp}
              className="w-full bg-gray-100 text-gray-700 py-3 px-6 rounded-lg font-medium hover:bg-gray-200 transition-colors"
            >
              Download App
            </button>
          </div>
        )}

        {status === 'redirecting' && (
          <div className="flex justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
          </div>
        )}

        <p className="text-xs text-gray-500 mt-6">
          Don't have the app? Download ReferPal from your app store to get started.
        </p>
      </div>
    </div>
  );
}
